/**
 * FEAT-087 — per-user date/time formatting.
 *
 * The redesign makes timestamp format a user setting with three regional
 * options, and fixes the order everywhere: date first, then time.
 *
 *   | setting  | order       | clock   | sample              |
 *   | -------- | ----------- | ------- | ------------------- |
 *   | us       | month first | 12-hour | Aug 28 · 4:20 PM    |
 *   | nlatam   | day first   | 12-hour | 28 Aug · 4:20 PM    |
 *   | slatam   | day first   | 24-hour | 28 Aug · 16:20      |  (default)
 *
 * **Why this takes a Date and not a string.** The design prototype shipped a
 * `dt(str, sep)` helper that re-formatted already-formatted strings with a
 * regex, and the handoff records what that cost: an earlier chained-replace
 * version matched its own output and produced `11:02 AM AM`. That whole bug
 * class only exists when the input is a formatted string. Here the input is
 * an instant (Date/ISO/epoch) and `Intl.DateTimeFormat` renders it once, so
 * there is no second pass to get wrong — the fix is structural, not a
 * carefully-ordered regex.
 *
 * Anything unparseable is returned unchanged rather than blanked, so a call
 * site that has not been migrated yet degrades to its old string instead of
 * rendering empty. `formatDateTime` on an already-formatted string is
 * therefore a no-op, which is the property the handoff's warning is really
 * about — see the tests.
 */

export const DATE_FORMAT_SETTINGS = ["us", "nlatam", "slatam"] as const;
export type DateFormatSetting = (typeof DATE_FORMAT_SETTINGS)[number];

/** Argentina · Chile · Uruguay — where this platform's first users are. */
export const DEFAULT_DATE_FORMAT: DateFormatSetting = "slatam";

export const DATE_FORMAT_LABELS: Record<DateFormatSetting, string> = {
  us: "United States",
  nlatam: "Mexico · Central America · Colombia",
  slatam: "Argentina · Chile · Uruguay",
};

export function isDateFormatSetting(value: unknown): value is DateFormatSetting {
  return (
    typeof value === "string" &&
    (DATE_FORMAT_SETTINGS as readonly string[]).includes(value)
  );
}

/** Falls back to the default for anything unrecognised (unset metadata, a
 * value written by an older client, a typo in a manual edit). */
export function coerceDateFormat(value: unknown): DateFormatSetting {
  return isDateFormatSetting(value) ? value : DEFAULT_DATE_FORMAT;
}

export type DateInput = Date | string | number | null | undefined;

/**
 * Month-first vs day-first is the only thing the locale decides here; the
 * clock is set explicitly below so it never depends on locale defaults.
 */
function localeFor(setting: DateFormatSetting): string {
  return setting === "us" ? "en-US" : "en-GB";
}

function uses12Hour(setting: DateFormatSetting): boolean {
  return setting !== "slatam";
}

// Intl.DateTimeFormat construction is expensive and these are hot in list
// renders, so instances are cached per (locale, options) pair.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, f);
  }
  return f;
}

/** null when the input is not a usable instant. */
function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Unparseable input passes through untouched (see the module header). */
function passthrough(value: DateInput): string {
  return typeof value === "string" ? value : "";
}

/** `Aug 28` / `28 Aug`, or with `withYear`: `Jun 2, 2026` / `2 Jun 2026`. */
export function formatDate(
  value: DateInput,
  setting: DateFormatSetting,
  opts: { withYear?: boolean } = {}
): string {
  const d = toDate(value);
  if (!d) return passthrough(value);
  return formatter(localeFor(setting), {
    day: "numeric",
    month: "short",
    ...(opts.withYear ? { year: "numeric" } : {}),
  }).format(d);
}

/** `4:20 PM` / `16:20`. */
export function formatTime(value: DateInput, setting: DateFormatSetting): string {
  const d = toDate(value);
  if (!d) return passthrough(value);
  return uses12Hour(setting)
    ? // en-US regardless of the day/month order: it is the variant that
      // renders an uppercase AM/PM, which is what the design shows.
      formatter("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(d)
    : // h23 rather than hour12:false — the latter can render midnight as 24:00.
      formatter("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
}

/**
 * Date first, then time — `Aug 28 · 4:20 PM`.
 *
 * `embedded` is for a timestamp sitting inside a middot-delimited metadata
 * line. There the two halves join with a space, so
 * `24 posts sent · last Aug 27 9:00 AM · voice overridden` reads as three
 * fields; joining with a middot would make it look like four.
 */
export function formatDateTime(
  value: DateInput,
  setting: DateFormatSetting,
  opts: { withYear?: boolean; embedded?: boolean } = {}
): string {
  const d = toDate(value);
  if (!d) return passthrough(value);
  const date = formatDate(d, setting, { withYear: opts.withYear });
  const time = formatTime(d, setting);
  return `${date}${opts.embedded ? " " : " · "}${time}`;
}

/** `Aug 24 – 30` / `24 – 30 Aug`; page headings depend on this following
 * the setting too ("The week of ..."). */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  setting: DateFormatSetting,
  opts: { withYear?: boolean } = {}
): string {
  const a = toDate(start);
  const b = toDate(end);
  if (!a || !b) return "";
  return formatter(localeFor(setting), {
    day: "numeric",
    month: "short",
    ...(opts.withYear ? { year: "numeric" } : {}),
  }).formatRange(a, b);
}
